import {
  Metric,
  MetricDataFilters,
  MetricExportProfileMetricConfig,
} from '../../../hooks/analytics'
import {
  cloneMetricDataFilters,
  sanitizeMetricDataFilters,
} from './metricDataFilters'

export type MetricFacetScalarValue = string | boolean
export type MetricFacetValues = Record<string, MetricFacetScalarValue[]>

export type MetricSelectionState = {
  id: number
  selected: boolean
  useDefaults: boolean
  facetValues: MetricFacetValues
}

export type HazardFiltersByHazard = Record<string, MetricDataFilters>

export function buildEmptyMetricSelection(metricId: number): MetricSelectionState {
  return {
    id: metricId,
    selected: false,
    useDefaults: true,
    facetValues: {},
  }
}

export function toStringList(value: unknown): string[] {
  const list = Array.isArray(value) ? value : value != null ? [value] : []
  const normalized = list.map(item => String(item).trim()).filter(item => item.length > 0)
  return Array.from(new Set(normalized))
}

export function toBooleanList(value: unknown): boolean[] {
  const list = Array.isArray(value) ? value : value != null ? [value] : []
  const normalized = list
    .map(item => {
      if (typeof item === 'boolean') return item
      if (typeof item === 'string') {
        const lowered = item.trim().toLowerCase()
        if (lowered === 'true') return true
        if (lowered === 'false') return false
      }
      return null
    })
    .filter((item): item is boolean => item !== null)

  return Array.from(new Set(normalized))
}

export function coerceDefaultsForFacetLists(metric: Metric): MetricFacetValues {
  const out: MetricFacetValues = {}
  const schema = metric.facet_schema || {}
  const defaults = metric.facet_defaults || {}

  for (const [key, def] of Object.entries(schema)) {
    const rawValue = defaults[key]
    out[key] = def.type === 'bool' ? toBooleanList(rawValue) : toStringList(rawValue)
  }

  return out
}

export function cleanseFacetFiltersForPayload(
  values: MetricFacetValues,
  metric: Metric,
): Record<string, unknown> | undefined {
  const cleaned: Record<string, unknown> = {}
  const schema = metric.facet_schema || {}

  for (const [key, def] of Object.entries(schema)) {
    const raw = values[key]
    if (raw === undefined) continue

    if (def.type === 'bool') {
      const arr = toBooleanList(raw)
      if (arr.length) cleaned[key] = arr
      continue
    }

    const arr = toStringList(raw)
    if (arr.length) cleaned[key] = arr
  }

  return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

export function toFacetValuesFromProfileFilters(
  metric: Metric,
  filters: Record<string, unknown> | null | undefined,
): MetricFacetValues {
  if (!filters) return {}

  const out: MetricFacetValues = {}
  const schema = metric.facet_schema || {}
  for (const [key, def] of Object.entries(schema)) {
    const raw = filters[key]
    if (raw === undefined || raw === null) continue
    out[key] = def.type === 'bool' ? toBooleanList(raw) : toStringList(raw)
  }

  return out
}

export function sanitizeHazardFiltersByHazard(
  hazardFilters: Record<string, MetricDataFilters | null | undefined>,
): HazardFiltersByHazard {
  const cleanedEntries = Object.entries(hazardFilters)
    .map(([hazard, filters]) => {
      const normalizedHazard = hazard.trim()
      const cleanedFilters = sanitizeMetricDataFilters(filters)
      return normalizedHazard && cleanedFilters ? ([normalizedHazard, cleanedFilters] as const) : null
    })
    .filter((entry): entry is readonly [string, MetricDataFilters] => entry !== null)

  return Object.fromEntries(cleanedEntries)
}

export function cloneHazardFiltersByHazard(
  hazardFilters: Record<string, MetricDataFilters | null | undefined>,
): HazardFiltersByHazard {
  return sanitizeHazardFiltersByHazard(
    Object.fromEntries(
      Object.entries(hazardFilters).map(([hazard, filters]) => [hazard, cloneMetricDataFilters(filters)]),
    ),
  )
}

function buildSelectedMetricConfigs({
  metrics,
  selections,
  hazardFiltersByHazard,
  includeHazardFilters,
}: {
  metrics: Metric[]
  selections: Record<number, MetricSelectionState>
  hazardFiltersByHazard: HazardFiltersByHazard
  includeHazardFilters: boolean
}): MetricExportProfileMetricConfig[] {
  return metrics
    .filter(metric => !metric.hide && selections[metric.id]?.selected)
    .map(metric => {
      const selection = selections[metric.id]
      const facetFilters = !selection.useDefaults
        ? cleanseFacetFiltersForPayload(selection.facetValues, metric)
        : undefined
      const filters = includeHazardFilters
        ? sanitizeMetricDataFilters(hazardFiltersByHazard[metric.hazard])
        : undefined

      return {
        metric_id: metric.id,
        use_defaults: selection.useDefaults,
        ...(facetFilters ? { facet_filters: facetFilters } : {}),
        ...(filters ? { filters } : {}),
      }
    })
    .sort((left, right) => left.metric_id - right.metric_id)
}

export function buildMetricProfileConfigs({
  metrics,
  selections,
}: {
  metrics: Metric[]
  selections: Record<number, MetricSelectionState>
}): MetricExportProfileMetricConfig[] {
  return buildSelectedMetricConfigs({
    metrics,
    selections,
    hazardFiltersByHazard: {},
    includeHazardFilters: false,
  })
}

export function buildMetricExportConfigs({
  metrics,
  selections,
  hazardFiltersByHazard,
}: {
  metrics: Metric[]
  selections: Record<number, MetricSelectionState>
  hazardFiltersByHazard: HazardFiltersByHazard
}): MetricExportProfileMetricConfig[] {
  return buildSelectedMetricConfigs({
    metrics,
    selections,
    hazardFiltersByHazard,
    includeHazardFilters: true,
  })
}

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(item => stableStringify(item)).join(',')}]`

  const entries = Object.entries(value as Record<string, unknown>).sort(([left], [right]) => left.localeCompare(right))
  return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item)}`).join(',')}}`
}

export function buildMetricProfileSnapshot({
  metrics,
  selections,
  hazardFiltersByHazard,
}: {
  metrics: Metric[]
  selections: Record<number, MetricSelectionState>
  hazardFiltersByHazard: HazardFiltersByHazard
}): string {
  return stableStringify({
    metrics: buildMetricProfileConfigs({ metrics, selections }),
    hazard_filters: sanitizeHazardFiltersByHazard(hazardFiltersByHazard),
  })
}
